/**
 * `useRunLimits` — what a run is limited to, asked of the server (#2286).
 *
 * The most tests a run will plan used to be a constant in the bundle
 * and a second constant on the server. Changing it meant editing both
 * and shipping a build, and editing one left the two disagreeing: raise
 * it on the server and the input still refused the bigger number; raise
 * it here and the run quietly clamped back down. The server owns the
 * number now and this asks for it.
 *
 * **It returns the number or nothing. Never a guess.** A fallback here
 * would be exactly the second copy that was removed, and on a server
 * set to something other than the old 5 the screen would state one
 * limit and then swap to another. Callers show no limit at all until
 * they have one — that is the contract, not an oversight.
 *
 * Built like `useOrgEntitlements`: one request shared by everyone who
 * mounts at once, cached for the session, and a failed request dropped
 * from the cache so the next caller retries instead of reusing the
 * rejection forever. Kept separate from that hook because it answers a
 * different question — those limits are what a customer has paid for
 * and vary per org, this one is set by whoever runs the server and is
 * the same for everybody.
 */
import { useEffect, useState } from 'react'
import { runLimitsAPI, type RunLimits } from '@/lib/api'

/** The in-flight or settled request, shared by every caller. */
let inFlight: Promise<RunLimits> | null = null

function fetchRunLimits(): Promise<RunLimits> {
  if (!inFlight) {
    inFlight = runLimitsAPI.get().catch(err => {
      // Drop the failure so a later mount retries rather than reusing
      // the rejection for the rest of the session.
      inFlight = null
      throw err
    })
  }
  return inFlight
}

/** Forget the cached limits — for tests, and for anywhere that changes
 * them and wants the next read to be fresh. */
export function invalidateRunLimits(): void {
  inFlight = null
}

/**
 * The most tests one run will plan, or `null` until the server says.
 *
 * `null` means "not known yet", which includes "the request failed".
 * Both cases want the same thing on screen: no number. A caller that
 * needs to clamp can still start a run safely without it, because the
 * server clamps whatever it is sent.
 */
export function useMaxTestCasesPerRun(): number | null {
  const [limit, setLimit] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchRunLimits()
      .then(l => { if (!cancelled) setLimit(l.maxTestCasesPerRun) })
      .catch(() => { if (!cancelled) setLimit(null) })
    return () => { cancelled = true }
  }, [])

  return limit
}
